import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "src/components/Displays/Text";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import { Box, List, Stack } from "@mui/material";
import { SMART_CHARGING_ROUTES } from "src/consts/Routes/SmartCharging";
import { EvsesCompatibility } from "src/models/smartChargingClientsModels";
import EvseListItem from "src/components/SmartCharging/Components/EvseListItem";
import { SESSION_KEYS, SMART_CHARGING_WALLBOX_DATA } from "src/consts/session";

const ModelSearch = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation("translation");
  const manufacturer = searchParams.get("manufacturer");

  const [getEvsesCompatibility] = useSessionStorage<EvsesCompatibility[]>(
    SESSION_KEYS.EVSES_COMPATIBILITY,
  );
  const [, setWallboxData] = useSessionStorage<SMART_CHARGING_WALLBOX_DATA>(
    SESSION_KEYS.SMART_CHARGING_WALLBOX_DATA,
  );
  const [getTransferId] = useSessionStorage<string>(SESSION_KEYS.TRANSFER_ID);

  const [modelsList, setModelsList] = useState<string[]>([]);

  useEffect(() => {
    const evsesCompatibility = getEvsesCompatibility();
    if (evsesCompatibility) {
      const filteredManufacturers = evsesCompatibility.filter(
        (evse) =>
          evse.manufacturer === manufacturer &&
          (evse.compatibilityStatus === "BASIC" ||
            evse.compatibilityStatus === "ADVANCED"),
      );
      const modelsList = filteredManufacturers.map((evse) => evse.modelName);
      const modelsListDistinct = modelsList.filter(
        (model, index, list) => list.indexOf(model) === index,
      );

      setModelsList(modelsListDistinct);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleModelClick = async (model: string) => {
    const transferId = getTransferId();
    const evsesCompatibility = getEvsesCompatibility();

    if (!transferId || !evsesCompatibility || !manufacturer) return "";

    const filteredManufacturer = evsesCompatibility.find(
      (evse) =>
        evse.manufacturer.toLowerCase() === manufacturer.toLowerCase() &&
        evse.modelName.toLowerCase() === model.toLowerCase(),
    );

    const payload = {
      manufacturer: filteredManufacturer?.manufacturer ?? "",
      modelName: filteredManufacturer?.modelName ?? "",
      compatibilityStatus: filteredManufacturer?.compatibilityStatus ?? "",
    };
    setWallboxData(payload);
    navigate(SMART_CHARGING_ROUTES.STATUS);
  };

  return (
    <Stack gap={5}>
      <Text
        text={t("page.wallboxCompatibilityModelSearch.formTitle")}
        sx={(theme) => ({
          fontSize: "24px",
          fontWeight: "500",
          lineHeight: "28px",
          fontFamily: "officina",
          color: theme.palette.mainFont.primary,
        })}
      />

      <Box
        sx={(theme) => ({
          px: "16px",
          borderRadius: "12px",
          bgcolor: theme.palette.white.main,
        })}
      >
        <List>
          {modelsList &&
            modelsList.map((model, index) => (
              <EvseListItem
                key={`${model}-${index}`}
                itemText={model}
                divider={index < modelsList.length - 1}
                handleItemClick={() => handleModelClick(model)}
              />
            ))}
        </List>
      </Box>
    </Stack>
  );
};

export default ModelSearch;
